import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, SmartDataTable, Badge, Modal, Input, InputAmount, Textarea } from '@/backoffice/components/ui';
import {
  fetchPromotions,
  createPromotion,
  deletePromotion,
  type AdminPromotion,
  type AdminPromotionStats,
} from '@/domains/admin/services/admin.service';
import { formatCurrency, formatDate } from '@/backoffice/utils';
import { Plus, Loader2 } from 'lucide-react';

export function AdminPromotionsPage() {
  const [promotions, setPromotions] = useState<AdminPromotion[]>([]);
  const [stats, setStats] = useState<AdminPromotionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage' as 'percentage' | 'fixed',
    discount_value: 10,
    max_uses: 100,
    active: true,
  });

  const load = () => {
    setLoading(true);
    fetchPromotions()
      .then(({ promotions: list, stats: s }) => {
        setPromotions(list);
        setStats(s);
      })
      .catch(() => {
        setPromotions([]);
        setStats(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!form.code.trim()) return;
    setSaving(true);
    try {
      await createPromotion(form);
      setShowModal(false);
      setForm({ code: '', description: '', discount_type: 'percentage', discount_value: 10, max_uses: 100, active: true });
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    await deletePromotion(id);
    load();
  };

  if (loading) {
    return (
      <DashboardLayout userType="admin" title="Promotions">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="admin" title="Promotions">
      <div className="flex justify-between mb-6">
        <p className="text-sm text-gray-500">Créez et gérez vos campagnes marketing</p>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowModal(true)}>Nouvelle promotion</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-blue-50 border-blue-200"><p className="text-sm text-blue-700">Total</p><p className="text-2xl font-bold">{stats?.total ?? 0}</p></Card>
        <Card className="bg-emerald-50 border-emerald-200"><p className="text-sm text-emerald-700">Actives</p><p className="text-2xl font-bold">{stats?.active ?? 0}</p></Card>
        <Card className="bg-amber-50 border-amber-200"><p className="text-sm text-amber-700">Utilisations</p><p className="text-2xl font-bold">{stats?.total_uses ?? 0}</p></Card>
        <Card className="bg-purple-50 border-purple-200"><p className="text-sm text-purple-700">Économies estimées</p><p className="text-2xl font-bold">{formatCurrency(stats?.estimated_savings ?? 0)}</p></Card>
      </div>

      <Card>
        <SmartDataTable
          data={promotions}
          columns={[
            { key: 'code', title: 'Code', render: (item) => <span className="font-mono font-semibold text-blue-600">{item.code}</span> },
            { key: 'description', title: 'Description', render: (item) => <p className="text-sm truncate max-w-xs">{item.description}</p> },
            {
              key: 'discount_value',
              title: 'Réduction',
              render: (item) => (
                <Badge variant="success">
                  {item.discount_type === 'percentage' ? `${item.discount_value}%` : formatCurrency(item.discount_value)}
                </Badge>
              ),
            },
            { key: 'used_count', title: 'Utilisations', render: (item) => `${item.used_count}${item.max_uses ? ` / ${item.max_uses}` : ''}` },
            {
              key: 'valid_until',
              title: 'Validité',
              render: (item) => item.valid_until ? formatDate(item.valid_until) : '—',
            },
            { key: 'active', title: 'Statut', render: (item) => <Badge variant={item.active ? 'success' : 'default'}>{item.active ? 'Active' : 'Inactive'}</Badge> },
          ]}
          keyExtractor={(item) => String(item.id)}
          onDelete={(item) => handleDelete(item.id)}
          emptyMessage="Aucune promotion"
        />
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Nouvelle promotion">
        <div className="space-y-4">
          <Input label="Code promo" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} />
          <Textarea label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          <div className="flex gap-2">
            {(['percentage', 'fixed'] as const).map((t) => (
              <Button key={t} variant={form.discount_type === t ? 'primary' : 'outline'} onClick={() => setForm((f) => ({ ...f, discount_type: t }))}>
                {t === 'percentage' ? 'Pourcentage' : 'Montant fixe'}
              </Button>
            ))}
          </div>
          <InputAmount label="Valeur" value={form.discount_value} onChange={(v) => setForm((f) => ({ ...f, discount_value: v }))} />
          <InputAmount label="Utilisations max" value={form.max_uses} onChange={(v) => setForm((f) => ({ ...f, max_uses: v }))} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button isLoading={saving} onClick={handleCreate}>Créer</Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
}
